import React, { Component } from "react";

import PropTypes from "prop-types";
// VERSÃO TRAVADA EM 2.5.5 (--save-exact). NÃO ATUALIZAR sem trocar o bundler.
//
// O react-scripts 4.0.3 deste projeto roda webpack 4, e o webpack 4 prefere o
// campo `module` do pacote. Da 2.7 em diante o Joyride publica esse campo como
// `dist/index.mjs`, e a extensão .mjs liga o modo ESM estrito do webpack 4 — que
// então falha ao ver o pacote importar named export de dependência CJS:
//   3.x   -> "Can't import the named export 'arrow' from non EcmaScript module"
//   2.9.3 -> "Can't import the named export 'Component' from non EcmaScript module"
// A 2.5.5 publica `es/index.js` (ESM de Babel, sem .mjs), que o webpack 4 trata
// pelas regras normais de interop e empacota sem reclamar. Peer é `15 - 18`,
// então cobre o React 18 daqui.
//
// Quando o projeto subir para react-scripts 5 / webpack 5, dá para ir para a 3.x
// — mas lá o componente virou NAMED export (`import { Joyride }`), e trocar sem
// ajustar esta linha dá "element type is invalid: got object".
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { bindActionCreators } from "redux";

import aguardarAlvo, { aguardarVisivel } from "./aguardarAlvo";
import {
  abrirDropdowns,
  alvoUsavel,
  criarVerificador,
  estaEmSidebarOculta,
  estaNaSidebar,
  resolverAlvo,
  sidebarExibida
} from "./alvo";
import { inscrever } from "./barramento";
import { paraJoyride, prepararPassos, proximoApos } from "./filtro";
import { CONFIG_FLOATER, rolarAteAlvo } from "./posicionamento";
import { ESPERA_ALVO_MS, roteiroDoPapel } from "./roteiros";
import { setMenu } from "../../actions/menuActions";

import "./tour.css";

/* ============================================================================
   Tour — ORQUESTRADOR (serve a todos os papéis)
   ============================================================================
   Um só motor; o que muda por papel é a lista de passos, escolhida em
   ./roteiros. Papel sem roteiro não monta nada.

   Mora em layout/Main.js, acima do <Switch>, porque a jornada atravessa várias
   telas: se o tour vivesse dentro de uma página, ele seria desmontado na
   primeira navegação e o estado morreria com ele.

   O tour é CONTROLADO: `stepIndex` e `run` são nossos, o Joyride só desenha. É o
   que permite pausar entre passos para esperar um alvo que ainda não carregou —
   no modo automático o Joyride tentaria desenhar no vazio.

   O AVANÇO É PELA NAVEGAÇÃO REAL, não pelo clique. Em passo de clique guiado
   quem manda o tour adiante é a chegada na rota (`componentDidUpdate`), e não o
   evento do botão. Isso vale por dois motivos: quem conhece outro caminho até a
   tela também avança, e não há um "Próximo" ensinando um caminho que não é o
   caminho.

   ESTADOS POSSÍVEIS
   - parado    -> `rodando: false`, `pausado: false`
   - andando   -> `rodando: true`
   - esperando -> `rodando: false` enquanto `aguardarAlvo` não resolve
   - pausado   -> `pausado: true`, quando a pessoa sai do roteiro

   Nada aqui é persistido, e isso é de propósito (ver ./barramento.js).
   ========================================================================== */

/**
 * Abaixo deste ponto a sidebar do CoreUI vira overlay, e o balão não cabe ao lado
 * dela: com ~256px de sidebar numa tela de 455px sobram ~200px, e o `maxWidth` do
 * balão é 300px. É o breakpoint `lg` do Bootstrap 4, o mesmo que o CoreUI usa.
 */
const LARGURA_LATERAL_MINIMA = 992;

const telaEstreita = () =>
  typeof window !== "undefined" && window.innerWidth < LARGURA_LATERAL_MINIMA;

/** Identidade de navegação: a query importa, e é o que separa "Minhas trilhas"
    (/activity/list?trail=true) de "Atividades" (/activity/list) — as duas têm o
    MESMO pathname. Sem a busca na conta, ir de uma para a outra não seria vista
    como navegação e o tour não avançaria. */
const localAtual = location => `${location.pathname}${location.search || ""}`;

/**
 * A navegação atual satisfaz o que o passo pedia?
 *
 * - **prefixo**: `/inteligencia-educacional` aceita
 *   `/inteligencia-educacional/professor`, porque a seção redireciona para a
 *   sub-rota do perfil assim que abre (ver pages/InteligenciaEducacional/App.jsx).
 *   Sem isso o tour pausaria no próprio redirecionamento que ele provocou.
 * - **query**: se o passo declara parâmetros, todos precisam bater. Se não
 *   declara, a query do momento é ignorada — telas acrescentam filtro à URL por
 *   conta própria, e exigir busca vazia deixaria o passo preso.
 */
export function rotaCombina(rotaEsperada, location) {
  if (!rotaEsperada) return false;

  const [caminhoEsperado, queryEsperada] = rotaEsperada.split("?");
  const { pathname } = location;

  const caminhoBate =
    pathname === caminhoEsperado ||
    pathname.startsWith(`${caminhoEsperado}/`) ||
    // Barra final do backend/menu não deve decidir se o passo avança.
    `${pathname}/` === caminhoEsperado;

  if (!caminhoBate) return false;
  if (!queryEsperada) return true;

  const atual = new URLSearchParams(location.search || "");

  return [...new URLSearchParams(queryEsperada).entries()].every(
    ([chave, valor]) => atual.get(chave) === valor
  );
}

const TEXTOS = {
  back: "Voltar",
  close: "Fechar",
  last: "Concluir",
  next: "Próximo",
  skip: "Sair do tour"
};

class Tour extends Component {
  constructor(props) {
    super(props);

    this.state = {
      passos: [],
      indice: 0,
      rodando: false,
      pausado: false
    };

    // Uma espera em curso pode terminar depois de o tour ser encerrado;
    // sem este selo, o `setState` tardio reabriria um tour já fechado.
    this.execucao = 0;
    this.desinscrever = null;

    // Alvos achados pelo NOME visível, por id de passo. Só entram aqui os que
    // nenhum seletor descreve, e é o que o `paraJoyride` recebe como elemento.
    this.alvosResolvidos = {};
  }

  componentDidMount() {
    this.desinscrever = inscrever(this.iniciar);
  }

  componentDidUpdate(prevProps) {
    if (localAtual(prevProps.location) === localAtual(this.props.location)) {
      return;
    }
    if (!this.state.rodando && !this.state.pausado) return;

    const passo = this.state.passos[this.state.indice];
    if (!passo) return;

    // Chegou onde o passo pedia: adiante.
    if (rotaCombina(passo.rotaEsperada, this.props.location)) {
      this.irPara(this.state.indice + 1);
      return;
    }

    if (!this.state.rodando) return;

    // Navegou para outro lugar. Só pausa se o alvo do passo atual foi embora com
    // a tela — pausar em toda mudança de rota era frágil demais: um redirect da
    // própria seção (a Inteligência Educacional manda /… para /…/professor) ou
    // um filtro que a tela põe na URL derrubavam o tour sem a pessoa ter
    // saído do roteiro. Alvo de menu continua no lugar, e o passo segue válido:
    // "clique aqui" não deixa de ser verdade porque ele foi ver outra coisa.
    if (!resolverAlvo(passo)) {
      this.setState({ rodando: false, pausado: true });
    }
  }

  componentWillUnmount() {
    this.execucao += 1;
    if (this.desinscrever) this.desinscrever();
  }

  /**
   * O roteiro do papel ativo, ou `null` se este papel não tem tour.
   *
   * É consultado a cada uso em vez de guardado no estado porque o papel ativo é
   * TROCÁVEL sem recarregar a página (o usuário pode ter mais de um), e o tour
   * precisa passar a ser o do papel novo.
   */
  roteiro() {
    return roteiroDoPapel(this.props.general.active_roles?.role_id);
  }

  iniciar = () => {
    const roteiro = this.roteiro();
    if (!roteiro) return;

    const passos = prepararPassos(roteiro, criarVerificador(document));

    this.avisarPassosDescartados(roteiro, passos);

    // Um tour de um passo só é o passo de boas-vindas sozinho: o menu deste
    // cliente não tem nenhuma das seções da jornada. Melhor não abrir nada do
    // que abrir um tour que não mostra lugar nenhum.
    if (passos.length < 2) return;

    this.execucao += 1;
    this.alvosResolvidos = {};
    this.setState({ passos, indice: 0, rodando: false, pausado: false }, () =>
      this.irPara(0)
    );
  };

  /**
   * Diz no console quais passos ficaram de fora e qual alvo não foi achado.
   *
   * Existe porque o menu vem do backend: quando um passo não aparece, ninguém
   * consegue saber daqui se o item falta para aquele perfil ou se o caminho é
   * outro. Sem este aviso, a investigação viraria adivinhação — foi o que
   * aconteceu com Conteúdos e Avaliações ADR. Segue o precedente da trilha
   * lúdica, que também avisa no console em vez de falhar calada.
   */
  avisarPassosDescartados(roteiro, passosMontados) {
    const restantes = new Set(passosMontados.map(passo => passo.id));
    const fora = roteiro.filter(passo => !restantes.has(passo.id));

    if (fora.length === 0) return;

    /* eslint-disable no-console */
    console.warn(
      `[tour] ${fora.length} de ${roteiro.length} passos ficaram de fora ` +
        `neste perfil:`,
      fora.map(passo => ({
        passo: passo.id,
        alvo: passo.alvo,
        porTexto: passo.alvoPorTexto,
        motivo: passo.verificarNoInicio
          ? "alvo não encontrado na tela"
          : `depende de "${passo.dependeDe}", que também ficou de fora`
      }))
    );
    /* eslint-enable no-console */
  }

  encerrar = () => {
    this.execucao += 1;
    this.setState({ rodando: false, pausado: false, indice: 0 });
  };

  retomar = () => {
    this.setState({ pausado: false }, () => this.irPara(this.state.indice));
  };

  /**
   * Traz para a tela um alvo que está no DOM mas não visível.
   *
   * A ORDEM IMPORTA: sidebar primeiro, dropdown depois. O dropdown vive dentro da
   * sidebar, e abrir de dentro para fora deixaria o toggle sendo clicado enquanto
   * o container ainda está fora da tela.
   *
   * A sidebar é aberta e NÃO é fechada depois — decisão de 03/08/2026 para o
   * celular. Fechar a cada passo daria quatro piscadas seguidas no roteiro do adm
   * escolar, que tem quatro passos de menu em sequência. Ficando aberta, ela cobre
   * a esquerda da tela, e o header segue visível à direita (é o que mantém os
   * badges de Ofensiva e Nível/XP alcançáveis no roteiro do aluno).
   *
   * Se uma navegação guiada fechar a sidebar no caminho, o passo seguinte que
   * precisar dela reabre — a checagem é por passo.
   */
  revelar(alvo) {
    if (estaEmSidebarOculta(alvo)) {
      this.props.setMenu(true);
    }

    abrirDropdowns(alvo);
  }

  /**
   * Recolhe a sidebar quando ela estaria ATRAPALHANDO o passo.
   *
   * É o contrário de `revelar`, e existe por dois sintomas do celular, onde a
   * sidebar é **overlay**:
   *
   * - o balão do passo aparece com o spotlight correto e **invisível atrás dela**
   *   (foi o caso do botão "Novo" da tabela);
   * - a rolagem até o alvo acontece **na página, atrás da sidebar** — porque alvo
   *   de conteúdo não tem container de rolagem próprio (o CoreUI não põe
   *   `overflow` em `.c-body`/`.c-main`; o conteúdo rola com o documento). Dá a
   *   impressão de que o tour rolou "para o lugar errado".
   *
   * Três condições, e cada uma tem motivo:
   *
   * - **só em tela estreita**: no desktop a sidebar não cobre nada, e fechá-la
   *   tiraria o menu de quem está usando;
   * - **só se o alvo NÃO é da sidebar**: senão desfaria o `revelar` do passo;
   * - **só se ela está aberta**: despachar `setMenu(false)` à toa dispara render.
   *
   * Passo centralizado fica de fora: ele não aponta para nada, então a sidebar
   * cobrindo o conteúdo é irrelevante, e fechar ali só somaria uma piscada.
   *
   * A consequência aceita: num roteiro que alterna menu e conteúdo — o do
   * professor — a sidebar abre e fecha ao longo do tour no celular. É inerente a
   * uma sidebar que é overlay. Alternativa sem vai-e-vem, se um dia incomodar:
   * reordenar o roteiro para agrupar os passos de menu de um lado e os de conteúdo
   * do outro, o que reduz as trocas a uma.
   *
   * @returns {boolean} pediu para recolher?
   */
  recolherSidebar(passo, alvo) {
    if (passo.posicao === "center") return false;
    if (!telaEstreita()) return false;
    if (estaNaSidebar(alvo)) return false;
    if (!sidebarExibida()) return false;

    this.props.setMenu(false);
    return true;
  }

  /**
   * Leva o tour ao passo `i`, esperando o alvo aparecer.
   *
   * Pula o passo cujo alvo não vier no tempo — inclusive em cascata, porque a
   * chamada recursiva passa pelo mesmo caminho.
   */
  irPara = async i => {
    const { passos } = this.state;

    if (i < 0) return;
    if (i >= passos.length) {
      this.encerrar();
      return;
    }

    const passo = passos[i];
    const execucao = this.execucao;

    this.setState({ rodando: false });

    const alvo = await aguardarAlvo(passo, ESPERA_ALVO_MS);

    // Encerrado (ou reiniciado) enquanto esperávamos.
    if (execucao !== this.execucao) return;

    if (!alvo) {
      // Leva junto quem dependia deste passo: sem isso, quatro botões de linha
      // ausentes viram quatro esperas de 8s seguidas.
      this.irPara(proximoApos(passos, i));
      return;
    }

    // O alvo pode estar no DOM sem estar na tela: dentro de dropdown fechado, ou
    // dentro da sidebar recolhida (que no celular é o estado normal). As duas
    // coisas são reveláveis — ver `revelar`.
    if (!alvoUsavel(alvo)) {
      this.revelar(alvo);
      const apareceu = await aguardarVisivel(alvo, alvoUsavel);

      if (execucao !== this.execucao) return;

      // Não apareceu: melhor pular do que desenhar um balão sobre item invisível.
      if (!apareceu) {
        this.irPara(proximoApos(passos, i));
        return;
      }
    }

    // A sidebar aberta no celular cobre o conteúdo. Se o alvo deste passo está no
    // conteúdo, ela precisa sair da frente ANTES de qualquer rolagem — senão a
    // página rola atrás dela, e é o que dá a impressão de o tour ter rolado para o
    // lugar errado.
    let podeRolar = true;

    if (this.recolherSidebar(passo, alvo)) {
      podeRolar = await aguardarVisivel(alvo, () => !sidebarExibida());

      if (execucao !== this.execucao) return;
    }

    // Estar visível não é estar à vista: numa sidebar longa — e no celular a tela
    // é curta — o item fica abaixo da dobra, e o balão nasceria ancorado num
    // elemento fora de vista, com a seta apontando para o nada. Roda sempre, e não
    // só quando o alvo estava oculto: no desktop a sidebar já está aberta e o
    // problema continua valendo para item fundo de lista.
    //
    // Duas exceções:
    //
    // - passo centralizado: o alvo dele é o `body` e o balão não é ancorado em
    //   nada. Rolar até o `body` numa página longa arrastava a página para o meio
    //   dela sem motivo, no primeiro passo do tour;
    // - sidebar que não recolheu: rolar agora seria rolar atrás do overlay. Melhor
    //   não rolar do que rolar onde ninguém vê.
    if (passo.posicao !== "center" && podeRolar) rolarAteAlvo(alvo);

    // Guardamos o elemento só quando nenhum seletor o descreve, isto é, quando
    // ele veio da busca por nome visível. Com seletor, deixamos o Joyride
    // reconsultar o DOM (ver `paraJoyride`).
    this.alvosResolvidos[passo.id] = resolverAlvo({ alvo: passo.alvo })
      ? null
      : alvo;

    this.setState({ indice: i, rodando: true, pausado: false });
  };

  tratarEvento = dados => {
    const { action, index, status, type } = dados;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      this.encerrar();
      return;
    }

    if (action === ACTIONS.CLOSE) {
      this.encerrar();
      return;
    }

    // O filtro inicial não alcança alvo de tela interna, e `aguardarAlvo` tem
    // timeout — mas se o alvo desaparecer entre a espera e o desenho, é aqui
    // que o tour se salva.
    if (type === EVENTS.TARGET_NOT_FOUND) {
      this.irPara(proximoApos(this.state.passos, index));
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      this.irPara(action === ACTIONS.PREV ? index - 1 : index + 1);
    }
  };

  render() {
    if (!this.roteiro()) return null;

    const { passos, indice, rodando, pausado } = this.state;
    const passo = passos[indice];

    return (
      <>
        {rodando && (
          <Joyride
            steps={passos.map(item =>
              paraJoyride(item, this.alvosResolvidos[item.id], {
                estreito: telaEstreita(),
                // Calculado no render, e não guardado: a rota muda embaixo do
                // tour, e um valor congelado no início do passo ficaria velho.
                jaEstaNaRota: rotaCombina(
                  item.rotaEsperada,
                  this.props.location
                )
              })
            )}
            stepIndex={indice}
            run={rodando}
            continuous
            showProgress={false}
            showSkipButton
            disableOverlayClose
            disableCloseOnEsc={false}
            scrollOffset={80}
            locale={TEXTOS}
            callback={this.tratarEvento}
            floaterProps={CONFIG_FLOATER}
            styles={{
              options: {
                primaryColor: "#02527d",
                textColor: "#023047",
                arrowColor: "#ffffff",
                overlayColor: "rgba(2, 48, 71, 0.55)",
                // Acima dos modais do CoreUI, que chegam a 1060 em
                // scss/_custom.scss. Sem isto o balão fica atrás do overlay.
                zIndex: 1100
              },
              tooltip: { borderRadius: 8, padding: 20 },
              tooltipTitle: { fontSize: 17, fontWeight: 600, marginBottom: 8 },
              tooltipContent: { fontSize: 14, lineHeight: 1.5, padding: 0 },
              buttonNext: {
                borderRadius: 4,
                fontSize: 14,
                padding: "8px 16px"
              },
              buttonBack: { color: "#02527d", fontSize: 14 },
              buttonSkip: { color: "#6c757d", fontSize: 13 }
            }}
          />
        )}

        {rodando && passo && (
          <div className="tour-contador">
            Passo {passo.indice} de {passo.total}
            {/* A dica só vale se ainda houver navegação a fazer. Já estando na
                tela, ela mandaria clicar em algo que não muda nada. */}
            {passo.rotaEsperada &&
              !rotaCombina(passo.rotaEsperada, this.props.location) && (
                <span className="tour-dica">
                  — clique no item destacado para seguir
                </span>
              )}
          </div>
        )}

        {pausado && (
          <div className="tour-pausado" role="status">
            <span>Tour pausado no passo {passo ? passo.indice : 1}.</span>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={this.retomar}
            >
              Retomar
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={this.encerrar}
            >
              Encerrar
            </button>
          </div>
        )}
      </>
    );
  }
}

Tour.propTypes = {
  general: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  setMenu: PropTypes.func.isRequired
};

const mapStateToProps = state => ({ general: state.general });

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ setMenu }, dispatch);
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Tour));
